import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Gift, Copy, Check, Share2, Users, ShieldCheck, Sparkles, Mail, MessageCircle, Loader2, Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  buildShareUrl,
  getReferralState,
  REFERRAL_CAP,
  REFERRAL_REWARD_CREDITS,
  type ReferralState,
} from '../services/referralsService';
import PhoneVerify from '../components/PhoneVerify';

/**
 * /referrals — the user's share-your-code page.
 *
 * The page has two top-level states, gated on phone verification:
 *
 *   UNVERIFIED (no phone yet) — shows the PhoneVerify gate + a locked preview
 *     of the share UI so the user knows what they're unlocking. Copy differs
 *     slightly between "you were invited" (bonus + own code) and "organic"
 *     (just your own code), but the gate is the same.
 *
 *   VERIFIED — shows the full share panel, progress stats, fair-play rules.
 *
 * This is the abuse prevention gate: a user cannot have a shareable code
 * without their own verified phone. Blocks fake-email signup rings from
 * spawning N referral codes from N free Google accounts.
 */
export default function Referrals() {
  const { user } = useAuth();
  const [state, setState] = useState<ReferralState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  const fetchState = async () => {
    try {
      const s = await getReferralState();
      setState(s);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Could not load referrals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchState(); }, []);

  if (!user) return null;

  const shareUrl = state?.code ? buildShareUrl(state.code) : '';
  const remaining = state ? Math.max(0, state.cap - state.rewardedCount) : 0;
  const capReached = state && state.rewardedCount >= state.cap;

  const copy = async (value: string, which: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      toast.success(which === 'link' ? 'Link copied' : 'Code copied');
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Could not copy — please long-press and copy manually.');
    }
  };

  const nativeShare = async () => {
    if (!state?.code) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'GradeLab — AI grading for students',
          text: `Join me on GradeLab and we both get ${REFERRAL_REWARD_CREDITS} free credits.`,
          url: shareUrl,
        });
      } catch { /* user cancelled — no-op */ }
    } else {
      copy(shareUrl, 'link');
    }
  };

  return (
    <div className="container-narrow py-10">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-brand-600 text-white grid place-items-center shadow-pop">
          <Gift className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold">Invite friends, get credits</h1>
          <p className="text-ink-600 mt-1">
            Give {REFERRAL_REWARD_CREDITS} credits. Get {REFERRAL_REWARD_CREDITS} credits.
            Up to {REFERRAL_CAP * REFERRAL_REWARD_CREDITS} credits from your first {REFERRAL_CAP} referrals.
          </p>
        </div>
      </div>

      {loading && (
        <div className="card p-8 mt-8 text-center text-ink-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Loading your referral status…
        </div>
      )}

      {error && !loading && (
        <div className="card p-6 mt-8 bg-red-50 border-red-100 text-red-700 text-sm">
          {error} — <button className="underline" onClick={() => { setLoading(true); fetchState(); }}>retry</button>
        </div>
      )}

      {state && !loading && (
        <>
          {/* UNVERIFIED state — show the phone gate. Copy varies based on whether
              the user was referred (bonus + code) or organic (just code). */}
          {!state.phoneVerified && (
            <>
              <div className="mt-8 card p-5 bg-amber-50 border-amber-100 flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <div className="font-semibold">Verify your phone to unlock your referral link</div>
                  <p className="mt-1 leading-relaxed">
                    {state.referredBy ? (
                      <>
                        Your welcome bonus of {REFERRAL_REWARD_CREDITS} credits is waiting, and your friend gets {REFERRAL_REWARD_CREDITS} too.
                        Phone verification activates both the bonus and your own shareable link.
                      </>
                    ) : (
                      <>
                        Sharing credits requires a verified phone number — this keeps the program fair for
                        real users and stops ring fraud. It's a one-time SMS, no charge to you.
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <PhoneVerify onDone={fetchState} referred={Boolean(state.referredBy)} />
              </div>

              {/* Locked preview — lets the user see what they're unlocking. */}
              <section className="mt-6 card p-6 opacity-60 pointer-events-none select-none">
                <h2 className="font-semibold flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Your share link (locked)
                </h2>
                <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2">
                  <input className="input font-mono text-sm" value="Your link will appear here after phone verification" readOnly />
                  <button className="btn-primary" disabled><Lock className="w-4 h-4" /> Locked</button>
                </div>
              </section>
            </>
          )}

          {/* VERIFIED state — the share panel is active. */}
          {state.phoneVerified && state.code && (
            <>
              {/* Claimed-bonus banner for referees */}
              {state.bonusAwarded && (
                <div className="mt-8 card p-4 bg-emerald-50 border-emerald-100 flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <strong>Welcome bonus claimed.</strong> Your {REFERRAL_REWARD_CREDITS} credits have been added —
                    now invite friends and earn {REFERRAL_REWARD_CREDITS} more for each one.
                  </div>
                </div>
              )}

              <section className="mt-8 card p-6">
                <h2 className="font-semibold flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Your share link
                </h2>
                <p className="text-sm text-ink-600 mt-1">
                  Anyone who signs up through this link and verifies their phone unlocks {REFERRAL_REWARD_CREDITS} credits —
                  and you get {REFERRAL_REWARD_CREDITS} too.
                </p>

                <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-2">
                  <input
                    className="input font-mono text-sm"
                    value={shareUrl}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button onClick={() => copy(shareUrl, 'link')} className="btn-primary">
                    {copied === 'link' ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-3 text-sm">
                  <span className="text-ink-500">Or share your code:</span>
                  <button
                    onClick={() => copy(state.code!, 'code')}
                    className="chip bg-ink-100 hover:bg-ink-200 text-ink-800 font-mono tracking-wide"
                    title="Click to copy"
                  >
                    {state.code} {copied === 'code' ? <Check className="w-3.5 h-3.5 ml-1" /> : <Copy className="w-3.5 h-3.5 ml-1" />}
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={nativeShare} className="btn-secondary btn-sm">
                    <Share2 className="w-4 h-4" /> Share…
                  </button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Check out GradeLab — AI grading for reports. Sign up with my link and we both get ${REFERRAL_REWARD_CREDITS} free credits: ${shareUrl}`)}`}
                    target="_blank" rel="noreferrer"
                    className="btn-secondary btn-sm"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent('Try GradeLab with me')}&body=${encodeURIComponent(`GradeLab is an AI grading tool I've been using. If you sign up with my link we both get ${REFERRAL_REWARD_CREDITS} free credits: ${shareUrl}`)}`}
                    className="btn-secondary btn-sm"
                  >
                    <Mail className="w-4 h-4" /> Email
                  </a>
                </div>
              </section>

              <section className="mt-6 grid sm:grid-cols-3 gap-4">
                <StatCard
                  icon={<Users className="w-5 h-5" />}
                  label="Friends who joined"
                  value={`${state.rewardedCount} / ${state.cap}`}
                  sub={capReached ? 'Cap reached — thank you!' : `${remaining} reward${remaining === 1 ? '' : 's'} left`}
                />
                <StatCard
                  icon={<Sparkles className="w-5 h-5" />}
                  label="Credits earned"
                  value={`${state.creditsEarned}`}
                  sub="From successful referrals"
                />
                <StatCard
                  icon={<ShieldCheck className="w-5 h-5" />}
                  label="Your bonus"
                  value={state.bonusAwarded ? 'Claimed' : state.referredBy ? 'Ready to claim' : '—'}
                  sub={state.bonusAwarded ? 'Welcome bonus credited' : state.referredBy ? `${REFERRAL_REWARD_CREDITS} credits credited` : 'You signed up without a referral'}
                />
              </section>
            </>
          )}

          <section className="mt-8 card p-6">
            <h2 className="font-semibold">How it works</h2>
            <ol className="mt-3 space-y-3 text-sm text-ink-700 list-decimal pl-5">
              <li>Verify your phone once — this activates your shareable link.</li>
              <li>Share your link or code with a friend.</li>
              <li>They sign up through the link with Google.</li>
              <li>They verify their own phone (one-time SMS).</li>
              <li>You each get {REFERRAL_REWARD_CREDITS} credits, instantly.</li>
            </ol>
            <div className="mt-4 text-xs text-ink-500 space-y-1">
              <p>
                <strong>Fair-play rules.</strong> Each phone number can only unlock the referral system on
                one GradeLab account, ever. A single account can earn credits from at most {REFERRAL_CAP} referrals
                ({REFERRAL_CAP * REFERRAL_REWARD_CREDITS} credits total). Referral codes only attach to new accounts —
                existing users can't retroactively claim a referral.
              </p>
              <p>
                We reserve the right to reverse credits on accounts showing signs of abuse (disposable numbers,
                rapid-fire signups from a single device, patterns that don't look like organic sharing).
                See our <Link to="/terms" className="underline">Terms</Link>.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-ink-500 text-sm">
        {icon} {label}
      </div>
      <div className="mt-2 text-2xl font-display font-bold text-ink-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-500">{sub}</div>}
    </div>
  );
}
